import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import * as FacetUtil from "./FacetUtil";
import * as TrackingUtil from "shared/src/utils/TrackingUtil";
import CustomerSettingsStore from "../stores/settings/CustomerSettingsStore";
import { SearchQuery, Query, SearchResultEx, SearchResultEntry } from "shared/src/types/articlesearch/SearchResult";
import { SearchStoreQuery } from "../stores/SearchStore";

type SearchPageProps = {
    location: {
        query: Query;
        state:
            | {
                  [x: string]: any;
              }
            | undefined
            | null;
    };
};

export function mapToCountedIds(ids: Array<string>): { [id: string]: number } {
    let i = 0;
    return (ids ?? []).reduce((map, id) => {
        map[id] = i++;
        return map;
    }, {} as { [k: string]: number });
}

export const DEFAULT_ROWS = 12;
export const DEFAULT_ROWS_LIST = 24;

export function getSearchResultRows(): number {
    return CountryUtil.getOptionalConfig()?.searchResultRows || 24;
}

export const SORT_CRITERIA = {
    relevance: {
        CRITERIA: "relevance",
        I18N_KEY: "EX_SEARCH_SORT_BY_RELEVANCE_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-relevance",
        DESCRIPTION: "relevance"
    },
    "price asc": {
        CRITERIA: "price asc",
        I18N_KEY: "EX_SEARCH_SORT_BY_PRICE_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-price",
        DESCRIPTION: "price_asc"
    },
    "price desc": {
        CRITERIA: "price desc",
        I18N_KEY: "EX_SEARCH_SORT_BY_PRICE_DESCENDING_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-price-descending",
        DESCRIPTION: "price_desc"
    },
    "description asc": {
        CRITERIA: "description asc",
        I18N_KEY: "EX_SEARCH_SORT_BY_A_TO_Z_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-a-to-z",
        DESCRIPTION: "a_to_z"
    },
    description: {
        CRITERIA: "description",
        I18N_KEY: "EX_SEARCH_SORT_BY_Z_TO_A_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-z-to-a",
        DESCRIPTION: "z_to_a"
    }
} as { [k: string]: { CRITERIA: string; I18N_KEY: string; TEST_CSS_CLASS: string; DESCRIPTION: string } };

export function enhanceWithOfferInfo(
    ids: Array<string>,
    resultMap: {
        [x: string]: SearchResultEntry;
    }
): Array<{
    idToFetch: string;
    variantId: string;
}> {
    return ids.map((id) => {
        let idToFetch;
        const r = resultMap[id];
        if (r && r.offers && r.offers.length > 0) {
            idToFetch = id + "@" + r.offers[0].id;
        } else {
            idToFetch = id;
        }
        return { idToFetch: idToFetch, variantId: id };
    });
}

// URL query => SearchQuery
export function buildQuery(query: Query, searchPurpose: string): SearchQuery {
    const roleDefaultSetting = !!CountryUtil.getOptionalConfig().defaultFilterOnlyCustomerAssortment;
    let role;
    if (roleDefaultSetting && SessionStore.isEmployee()) {
        role = query.role === "none" ? undefined : "customer";
    } else {
        role = query.role;
    }
    const facets = query.facets || "";
    const ranges = query.ranges || "";
    const options = query.options || "";
    const sorting = query.sorting;
    let optimizedQuery = "";
    if (query.q) {
        if (typeof query.q === "string") {
            optimizedQuery = query.q;
        } else {
            optimizedQuery = query.q[0];
        }
    }
    return {
        q: optimizedQuery || "*",
        categoryPath: constructCategoryPath(query),
        facets: FacetUtil.parseMultiFacetString(facets),
        ranges: FacetUtil.parseNumericFacetString(ranges),
        options: FacetUtil.parseBoolFacetString(options),
        profile: query.profile || undefined,
        role: role,
        sorting: sorting,
        searchPurpose: searchPurpose
    };
}

export function mergeSearchQueries(query: SearchQuery, additionalQuery: Partial<SearchQuery>): SearchQuery {
    return {
        q: query.q || additionalQuery.q || "",
        searchPurpose: query.searchPurpose || additionalQuery.searchPurpose,
        categoryPath: query.categoryPath || additionalQuery.categoryPath,
        facets: query.facets.concat(additionalQuery.facets || []),
        ranges: query.ranges.concat(additionalQuery.ranges || []),
        options: query.options.concat(additionalQuery.options || []),
        sorting: query.sorting || additionalQuery.sorting,
        profile: query.profile || additionalQuery.profile,
        role: query.role || additionalQuery.role,
        restrictionId: query.restrictionId || additionalQuery.restrictionId,
        onlySearchable: query.onlySearchable || additionalQuery.onlySearchable
    };
}

export function getSelectedSortCriteria(eventKey: string | undefined | null) {
    if (eventKey) {
        const result = SORT_CRITERIA[eventKey];
        if (result) {
            return result;
        }
    }
    return SORT_CRITERIA.relevance;
}

export function buildFilters(data: SearchStoreQuery): Array<string> {
    const filter = [];
    // TODO(FD) unify the different facet types?
    if (data.categoryPath) {
        filter.push("category:" + data.categoryPath);
    }
    for (const facet of data.facets || []) {
        for (const value of facet.values) {
            filter.push(facet.key + ":" + value);
        }
    }
    for (const rangeFacet of data.ranges || []) {
        filter.push(rangeFacet.key + ":[" + (rangeFacet.range ? rangeFacet.range.join("~") : "") + "]");
    }
    for (const boolFacet of data.options || []) {
        filter.push(boolFacet.key + ":" + (boolFacet.value ? "true" : "false"));
    }
    return filter;
}

type SearchLocation = {
    pathname: string;
    query: Query;
    state?: {} | null;
};

// Search query => URL query
export function createLocationForSearchPages(query: SearchQuery, path: string): SearchLocation {
    const roleDefaultSetting = !!CountryUtil.getOptionalConfig().defaultFilterOnlyCustomerAssortment;
    let role;
    if (roleDefaultSetting && SessionStore.isEmployee()) {
        role = query.role === "customer" ? undefined : "none";
    } else {
        role = query.role;
    }
    const pathParts = query.categoryPath ? query.categoryPath.split("/") : [];
    const newQuery = {
        q: query.q,
        sorting: query.sorting || undefined,
        facets: FacetUtil.buildMultiFacetQueryString(query.facets),
        ranges: FacetUtil.buildNumericFacetQueryString(query.ranges),
        options: FacetUtil.buildBoolFacetQueryString(query.options),
        profile: query.profile || undefined,
        role: role || undefined
    } as Query;
    // tiefkühl/speiseeis -> &cat1=tiefkühl&cat2=speiseeis
    pathParts.forEach((part: string, index: number) => {
        (newQuery as any)["cat" + (index + 1)] = part;
    });
    return {
        pathname: path,
        query: newQuery,
        state: {} // reset pagination state
    };
}

export function createLocationForCategoryPage(query: SearchQuery): SearchLocation {
    const sanitizedQuery = query.q === "*" ? undefined : query.q;
    return {
        pathname: "/category/" + (query.categoryPath || ""),
        query: {
            q: sanitizedQuery,
            facets: FacetUtil.buildMultiFacetQueryString(query.facets),
            ranges: FacetUtil.buildNumericFacetQueryString(query.ranges),
            options: FacetUtil.buildBoolFacetQueryString(query.options),
            sorting: query.sorting || undefined,
            profile: query.profile || undefined,
            role: query.role || undefined
        },
        state: {} // reset pagination state
    };
}

export function updatePaginationState(
    stateParamName: string,
    router: {
        location: {
            query: Query;
            pathname: string;
            state:
                | {
                      [x: string]: any;
                  }
                | undefined
                | null;
        };
        replace: any;
    }
) {
    const numPages = router.location.state?.[stateParamName] ?? 1;
    const newState = Object.assign({}, router.location.state || {});
    newState[stateParamName] = numPages + 1;
    const newLocation = Object.assign({}, router.location, { state: newState });
    router.replace(newLocation);
}

export function constructCategoryPath(query: Query): string {
    const pathParts: string[] = [];
    let index = 1;
    while ((query as any)["cat" + index]) {
        pathParts.push((query as any)["cat" + index]);
        index += 1;
    }
    return pathParts.join("/");
}

export function loadMoreButtonNeeded(results: SearchResultEx, searchRunning: boolean) {
    let page;
    let totalPages;
    try {
        page = parseInt(results.page as any, 10);
        totalPages = parseInt(results.totalPages as any, 10);
    } catch (e) {
        TrackingUtil.trackUiErrorSd("SearchUtil", "", e);
        return false;
    }

    const numLoaded = results.resultIds ? results.resultIds.length : 0;
    const allCurrentLoaded = results.result
        ? results.result.length === results.amount || results.result.length === numLoaded
        : false;

    return !searchRunning && allCurrentLoaded && totalPages > page;
}

export function getContextDependentAdditionalFilters(): string[] {
    // TODO(FD) the SearchResultStore currently can't use this version because it uses another data type that gets
    // transformed into this later. We should just simplify a lot of the SearchStore/SearchResultStore code and
    // hopefully then get rid of the duplication
    if (CustomerSettingsStore.isFsdFilterActive()) {
        return ["delivery_mode:METRO_DELIVERY"];
    } else {
        return [];
    }
}

export function sanitizeQuery(location: SearchPageProps["location"] & { search?: string }): {
    query: Query;
    state: { [p: string]: any } | undefined | null;
} & { search?: string } {
    const { query, search } = location;

    // Utility function to sanitize the `q` key in the `query` object
    const sanitizeQKey = (qValue: string): string => {
        return qValue.split("?q=")[0]; // Keep only the part before the first `?q=`
    };

    // Utility function to sanitize the entire `search` string
    const sanitizeSearch = (searchString: string): string => {
        const urlSearchParams = new URLSearchParams(searchString);

        // Collect all parameters and sanitize `q`
        const seenKeys = new Set<string>();
        for (const key of urlSearchParams.keys()) {
            if (key === "q") {
                seenKeys.add(key);
            }
        }

        return `?${urlSearchParams.toString()}`;
    };

    const sanitizedQuery = { ...query };
    let sanitizedSearch = search;

    // Handle q if it's an array
    if (sanitizedQuery.q) {
        if (Array.isArray(sanitizedQuery.q)) {
            // Take only the first value, then sanitize it
            sanitizedQuery.q = sanitizeQKey(sanitizedQuery.q[0]);
        } else if (typeof sanitizedQuery.q === "string") {
            sanitizedQuery.q = sanitizeQKey(sanitizedQuery.q);
        }
    }

    // Sanitize the `search` string to remove duplicate `q`
    if (sanitizedSearch) {
        sanitizedSearch = sanitizeSearch(sanitizedSearch);
    }

    return {
        ...location,
        query: sanitizedQuery,
        search: sanitizedSearch
    };
}

export function clearSecondQInURL() {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    let q = url.searchParams.get("q") || "";
    if (q.length !== 0) {
        q = q.split("?q=")[0];
        url.searchParams.set("q", q);
        if (currentUrl !== url.toString()) {
            window.history.replaceState({}, "", url.toString());
        }
    }
    return url;
}
